<script>
import { mapActions, mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { 
  isPhoneNumberValid, 
  isPhoneE164, 
  isPhoneNumberValidWithDialCode, 
  startsWithPlus 
} from 'shared/helpers/Validators';
import parsePhoneNumber from 'libphonenumber-js';
import { useVuelidate } from '@vuelidate/core';
import countries from 'shared/constants/countries.js';
import { directive as onClickaway } from 'vue-clickaway';

export default{
  directives: {
    onClickaway: onClickaway,
  },
  props: {
    isEditingBranch: {
      type: Boolean,
      default: false,
    },
    branchId: {
      type: String,
      default: null,
    },
    resetCreateBranch: {
      type: Function,
      default: () => {},
    }
  },
  data() {
    return{
      branchName: '',
      phoneNumber: '',
      address: '',
      activeDialCode: '',
      phoneInputTouched: false,
      selectedCountry: null,
      showDropdown: false,
      searchCountry: '',
      selectedIndex: -1,
      rawPhoneNumber: '',
      isCreatingBranch: false,
    }
  },
  setup() {
    return { v$: useVuelidate() };
  },
  async mounted(){
    try{
      // Auto-detect country based on browser locale
      this.detectCountry();
      
      if(this.isEditingBranch && this.branchId?.length !== 0){
        await this.fetchBranch(this.branchId);
        this.branchName = this.selectedBranch.branch_name;
        this.phoneNumber = this.selectedBranch.phone_number;
        this.address = this.selectedBranch.address;
        
        // Parse existing phone number to set the dial code
        this.parseExistingPhoneNumber();
      }
    }catch(err){
      console.error(err);
      useAlert(err);
    }
  },
  computed: {
    ...mapGetters('appointments', ['selectedBranch']),
    
    allCountries() {
      return countries;
    },

    parsePhoneNumber() {
      try {
        return parsePhoneNumber(this.phoneNumber || '');
      } catch (error) {
        return null;
      }
    },

    isPhoneNumberNotValid() {
      if (!this.phoneNumber) return false; // Empty is considered valid at this stage
      if (this.phoneNumber && !this.phoneInputTouched) return false; // Not touched yet

      try {
        const parsedNumber = parsePhoneNumber(this.phoneNumber);
        if (!parsedNumber) return true;
        return !(parsedNumber.isPossible() && parsedNumber.isValid());
      } catch (error) {
        console.error('Error validating phone number:', error);
        return true;
      }
    },

    phoneNumberError() {
      if (!this.phoneNumber) return '';

      if (this.activeDialCode === '' && this.phoneInputTouched) {
        return this.$t('BRANCHES.CREATE_MODAL.VALIDATION.MISSING_COUNTRY_CODE');
      }

      if (this.isPhoneNumberNotValid && this.phoneInputTouched) {
        if (this.phoneNumber.length < 8) {
          return this.$t('BRANCHES.CREATE_MODAL.VALIDATION.TOO_SHORT');
        } else if (!startsWithPlus(this.phoneNumber)) {
          return this.$t('BRANCHES.CREATE_MODAL.VALIDATION.MISSING_PLUS');
        } else {
          return this.$t('BRANCHES.CREATE_MODAL.VALIDATION.GENERIC_INVALID');
        }
      }

      return '';
    },

    formattedPhoneNumber() {
      if (!this.phoneNumber) return '';

      if (startsWithPlus(this.phoneNumber)) {
        return this.phoneNumber;
      }

      return this.activeDialCode 
        ? `${this.activeDialCode}${this.rawPhoneNumber}` 
        : this.rawPhoneNumber;
    },
  },
  methods: {
    ...mapActions('appointments', ['createBranch', 'editBranch', 'fetchBranch']),

    navigateToBranch(branchId) {
      this.$router.push({ name: 'viewBranch', params: { branchId } });
    },

    detectCountry() {
      try {
        const locale = navigator.language || navigator.userLanguage || 'en-US';
        const countryCode = locale.split('-')[1]?.toLowerCase();

        if (countryCode) {
          const country = this.allCountries.find(
            c => c.id.toLowerCase() === countryCode
          );

          if (country) {
            this.selectedCountry = country;
            this.activeDialCode = country.dial_code;
          }
        }
      } catch (error) {
        console.error('Error detecting country:', error); // TODO: i18n this (optional for logs)
      }
    },

    parseExistingPhoneNumber() {
      if (!this.phoneNumber) return;
      try {
        if (startsWithPlus(this.phoneNumber)) {
          const parsedPhone = parsePhoneNumber(this.phoneNumber);
          if (parsedPhone) {
            this.activeDialCode = `+${parsedPhone.countryCallingCode}`;
            this.rawPhoneNumber = parsedPhone.nationalNumber;
            const country = this.allCountries.find(
              c => c.dial_code === this.activeDialCode
            );
            if (country) {
              this.selectedCountry = country;
            }
          }
        }
      } catch (error) {
        console.error('Error parsing phone number:', error); // TODO: i18n this (optional)
      }
    },

    validatePhoneNumber() {
      this.phoneInputTouched = true;

      if (!this.phoneNumber) return false;

      try {
        let numberToValidate = this.phoneNumber;
        if (!startsWithPlus(this.phoneNumber) && this.activeDialCode && this.rawPhoneNumber) {
          numberToValidate = `${this.activeDialCode}${this.rawPhoneNumber}`;
        }

        const parsedNumber = parsePhoneNumber(numberToValidate);

        if (parsedNumber) {
          return parsedNumber.isPossible() && parsedNumber.isValid();
        }
      } catch (error) {
        console.error('Error validating phone number:', error); // TODO: i18n this (optional)
      }

      if (startsWithPlus(this.phoneNumber)) {
        return isPhoneE164(this.phoneNumber) && this.phoneNumber.length >= 5;
      }

      return false;
    },

    async handleCreateBranch() {
      if (this.isCreatingBranch) return;
      
      this.phoneInputTouched = true;

      if (!this.branchName || !this.address) {
        useAlert(this.$t('BRANCHES.CREATE_MODAL.VALIDATION.REQUIRED_FIELDS'), 'error');
        return;
      }

      if (!this.phoneNumber) {
        useAlert(this.$t('BRANCHES.CREATE_MODAL.VALIDATION.PHONE_REQUIRED'), 'error');
        return;
      }

      if (this.isPhoneNumberNotValid) {
        useAlert(this.phoneNumberError || this.$t('BRANCHES.CREATE_MODAL.VALIDATION.INVALID_PHONE'), 'error');
        return;
      }

      try {
        const payload = {
          'branch_name': this.branchName,
          'phone_number': this.formattedPhoneNumber,
          'address': this.address,
        };
        this.isCreatingBranch = true;
        const response = await this.createBranch(payload);
        useAlert(this.$t('BRANCHES.CREATE_MODAL.MESSAGES.CREATE_SUCCESS'), 'success');
        const branchId = response.branch_id;
        this.navigateToBranch(branchId);
      } catch (err) {
        console.error(err);
        useAlert(err);
        this.isCreatingBranch = false;
      } finally {
        this.resetCreateBranch();
      }
    },

    onPhoneNumberInputChange(event) {
      this.phoneInputTouched = true;
      this.rawPhoneNumber = event.target.value;

      if (this.activeDialCode && this.rawPhoneNumber) {
        this.phoneNumber = `${this.activeDialCode}${this.rawPhoneNumber}`;
      } else {
        this.phoneNumber = this.rawPhoneNumber;
      }

      this.validatePhoneNumber();
    },

    setDialCode() {
      if (!this.phoneNumber) return;

      try {
        const parsedPhone = parsePhoneNumber(this.phoneNumber);
        if (parsedPhone && parsedPhone.countryCallingCode) {
          this.activeDialCode = `+${parsedPhone.countryCallingCode}`;
          this.rawPhoneNumber = parsedPhone.nationalNumber;
        }
      } catch (error) {
        console.error('Error parsing phone number:', error); // TODO: i18n this (optional)
      }
    },

    setPhoneCode(code) {
      this.activeDialCode = code;

      if (this.rawPhoneNumber) {
        this.phoneNumber = `${code}${this.rawPhoneNumber}`;
      } else {
        this.phoneNumber = code;
      }

      this.validatePhoneNumber();
    },

    async handleEditBranch() {
      if(this.isCreatingBranch) return;

      this.phoneInputTouched = true;

      if (!this.branchName || !this.address) {
        useAlert(this.$t('BRANCHES.CREATE_MODAL.VALIDATION.REQUIRED_FIELDS'), 'error');
        return;
      }

      if (!this.phoneNumber) {
        useAlert(this.$t('BRANCHES.CREATE_MODAL.VALIDATION.PHONE_REQUIRED'), 'error');
        return;
      }

      if (this.isPhoneNumberNotValid) {
        useAlert(this.phoneNumberError || this.$t('BRANCHES.CREATE_MODAL.VALIDATION.INVALID_PHONE'), 'error');
        return;
      }

      try {
        const payload = {
          'branch_name': this.branchName,
          'phone_number': this.formattedPhoneNumber,
          'address': this.address,
        };
        
        this.isCreatingBranch = true;
        await this.editBranch({ branchId: this.branchId, payload });
        useAlert('Branch updated successfully', 'success'); // TODO: i18n this
      } catch (err) {
        console.error(err);
        useAlert(err); // TODO: i18n this
        this.isCreatingBranch = false;
      } finally {
        this.resetCreateBranch();
      }
    },

    hideCountryDropdown() {
      this.showDropdown = false;
      this.searchCountry = '';
    }
  }

}
</script>

<template>
  <div class="p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-w-md w-full mx-auto">
    <!-- Branch Name -->
    <div class="w-full mb-4">
      <label class="block">
        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ $t('BRANCHES.CREATE_MODAL.LABELS.BRANCH_NAME') }}
        </span>
        <div class="relative rounded-md mt-1"
          :class=" [
            branchName.length === 0 && phoneInputTouched
              ? 'border-red-400 dark:border-red-500'
              : 'border-gray-300 dark:border-gray-600'
          ]"
        >
          <input 
            type="text" 
            v-model="branchName" 
            :placeholder="$t('BRANCHES.CREATE_MODAL.PLACEHOLDERS.BRANCH_NAME')"
            class="w-full py-3 px-3 rounded-md text-gray-900 dark:text-white dark:bg-slate-700 bg-transparent outline-none"
            @focus="phoneInputTouched = true"
          />
        </div>
        
        <!-- Error message -->
        <div 
          v-if="branchName.length === 0 && phoneInputTouched" 
          class="mt-1 text-sm text-red-500"
        >
          {{ $t('BRANCHES.CREATE_MODAL.VALIDATION.BRANCH_NAME_REQUIRED') }}
        </div>
      </label>
    </div>

    <!-- Phone Number -->
    <div class="w-full mb-4">
      <label class="block">
        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ $t('BRANCHES.CREATE_MODAL.LABELS.PHONE_NUMBER') }}
        </span>
        <div class="relative border border-solid rounded-md mt-1">
          <!-- Country dropdown button -->
          <div 
            class="absolute inset-y-0 left-0 flex items-center pl-3 cursor-pointer"
            @click="showDropdown = !showDropdown"
          >
            <span v-if="selectedCountry && selectedCountry.emoji" class="mr-1">
              {{ selectedCountry.emoji }}
            </span>
            <span v-else>🌐</span>
            <span v-if="activeDialCode" class="ml-1 text-sm">{{ activeDialCode }}</span>
            <span class="ml-1">▼</span>
          </div>
          
          
          <!-- Phone number input -->
          <input
            type="tel"
            v-model="rawPhoneNumber"
            :placeholder="$t('BRANCHES.CREATE_MODAL.PLACEHOLDERS.PHONE_NUMBER')"
            class="w-full py-3 pl-20 pr-3 rounded-md text-gray-900 dark:text-white dark:bg-slate-700 bg-transparent outline-none"
            @input="onPhoneNumberInputChange($event)"
            @focus="phoneInputTouched = true"
          />
          
          <!-- Country dropdown -->
          <div 
            v-if="showDropdown"
            v-on-clickaway="hideCountryDropdown"
            class="absolute z-50 left-0 top-full mt-1 w-64 max-h-64 overflow-y-auto bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg"
          >
            <input
              type="text"
              v-model="searchCountry"
              :placeholder="$t('BRANCHES.CREATE_MODAL.PLACEHOLDERS.SEARCH_COUNTRIES')"
              class="w-full p-2 border-b border-gray-300 dark:border-gray-600 dark:bg-slate-700 dark:text-white"
            />
            <div class="divide-y divide-gray-200 dark:divide-gray-600">
              <div
                v-for="country in allCountries.filter(c => 
                  c.name.toLowerCase().includes(searchCountry.toLowerCase()) || 
                  c.dial_code.includes(searchCountry)
                )"
                :key="country.id"
                @click="setPhoneCode(country.dial_code); showDropdown = false; selectedCountry = country;"
                class="p-2 flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600"
                :class="{ 'bg-blue-50 dark:bg-blue-900': country.dial_code === activeDialCode }"
              >
                <span class="mr-2">{{ country.emoji }}</span>
                <span class="dark:text-white">{{ country.name }}</span>
                <span class="ml-auto text-gray-500 dark:text-gray-400">{{ country.dial_code }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Error message -->
        <div 
          v-if="isPhoneNumberNotValid && phoneInputTouched" 
          class="mt-1 text-sm text-red-500"
        >
          {{ phoneNumberError }} <!-- Already dynamic / may be internationalized -->
        </div>
        
        <!-- Help text -->
        <div 
          class="mt-1 text-sm text-gray-500 dark:text-gray-400"
          :class="{'text-yellow-600 dark:text-yellow-400': phoneInputTouched && !phoneNumber}"
        >
          {{ $t('BRANCHES.CREATE_MODAL.VALIDATION.PHONE_FORMAT') }}
        </div>
      </label>
    </div>

    <!-- Address -->
    <div class="w-full mb-4">
      <label class="block">
        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ $t('BRANCHES.CREATE_MODAL.LABELS.ADDRESS') }}
        </span>
        <div class="relative rounded-md mt-1">
          <input 
            type="text" 
            v-model="address" 
            :placeholder="$t('BRANCHES.CREATE_MODAL.PLACEHOLDERS.ADDRESS')"
            class="w-full py-3 px-3 rounded-md text-gray-900 dark:text-white dark:bg-slate-700 bg-transparent outline-none"
            @focus="phoneInputTouched = true"
          />
        </div>
        
        <!-- Error message -->
        <div 
          v-if="address.length === 0 && phoneInputTouched" 
          class="mt-1 text-sm text-red-500"
        >
          {{ $t('BRANCHES.CREATE_MODAL.VALIDATION.ADDRESS_REQUIRED') }}
        </div>
      </label>
    </div>
    
    <!-- Action Button -->
    <div class="flex justify-between mt-4 gap-4">
      <button 
        @click="resetCreateBranch()"
        class="w-full py-2 mt-2 text-white font-medium rounded-md transition 
          bg-slate-600 hover:bg-slate-700 focus:ring-2 focus:ring-slate-400 focus:outline-none"
      >
        {{ $t('BRANCHES.CREATE_MODAL.BUTTONS.CANCEL') }}
      </button>
      <button 
        @click="isEditingBranch ? handleEditBranch() : handleCreateBranch()"
        :disabled="isCreatingBranch"
        class="w-full py-2 mt-2 text-white font-medium rounded-md transition 
          bg-woot-600 hover:bg-woot-700 focus:ring-2 focus:ring-woot-400 focus:outline-none"
      >
        {{ isEditingBranch ? $t('BRANCHES.CREATE_MODAL.BUTTONS.EDIT') : $t('BRANCHES.CREATE_MODAL.BUTTONS.CREATE') }}
      </button>
    </div>
  </div>
</template>
